import { Link, usePage } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import SettingsSubNavMobile from "@/components/settings/settings-sub-nav-mobile"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import {
  agencySettingsBrandsPath,
  agencySettingsProfilePath,
  agencySettingsTeamPath,
} from "@/routes"

/**
 * OKS-40 — Vertical sub-nav for the Agency Settings shell.
 *
 * V1 sub-pages:
 *   - Profile (OKS-40)         — agency-side editable
 *   - Team    (OKS-672)        — internal tab at /agency/settings/team
 *   - Brands  (OKS-854)        — internal tab at /agency/settings/brands
 *
 * Mirrors the Brand sub-nav layout exactly so an agency user
 * recognises the pattern when they switch contexts.
 *
 * OKS-854 — Brands was the LAST external cross-link here, so the `external`
 * flag and its "↗" glyph are now unused by every item. The flag is kept on
 * the `NavItem` shape (and still honoured in the render) because the
 * pattern is shared with the Brand-side sub-nav; it simply has no active
 * consumer on the agency side today.
 */

interface NavItem {
  title: string
  href: string
  testId: string
  external?: boolean
}

export function AgencySettingsNav() {
  const { t } = useTranslation()
  const { url } = usePage()

  const items: readonly NavItem[] = [
    {
      title: t("settings.agency.nav.profile"),
      href: agencySettingsProfilePath(),
      testId: "agency-settings-nav-profile",
    },
    {
      // OKS-672 — Team relocated into Agency Settings as a first-class
      // internal tab (was an external cross-link ↗ to /agency/team).
      // Active-state highlight now resolves against `agencySettingsTeamPath`.
      title: t("settings.shared.nav.team"),
      href: agencySettingsTeamPath(),
      testId: "agency-settings-nav-team",
    },
    {
      // OKS-854 — Brands relocated into Agency Settings as a first-class
      // internal tab (was an external cross-link ↗ to /agency/brands, which
      // now 301s here). Same treatment OKS-672 gave Team: active-state
      // highlight resolves against `agencySettingsBrandsPath`, and the
      // "opens elsewhere" affordance is gone along with the external
      // semantics (UX Spec §2).
      title: t("settings.shared.nav.brands"),
      href: agencySettingsBrandsPath(),
      testId: "agency-settings-nav-brands",
    },
  ]

  return (
    <>
      <SettingsSubNavMobile
        items={items.map((i) => ({
          label: i.title,
          href: i.href,
          testId: `${i.testId}-mobile`,
        }))}
        currentHref={url}
        ariaLabel={t("settings.agency.nav.section")}
        testId="agency-settings-nav-mobile"
      />
      <nav
        data-testid="agency-settings-nav"
        aria-label={t("settings.agency.nav.section")}
        className="hidden flex-col space-y-1 space-x-0 md:flex"
      >
        {items.map((item) => {
          const active = url === item.href || url.startsWith(`${item.href}?`)
          return (
            <Button
              key={item.href}
              size="sm"
              variant="ghost"
              asChild
              className={cn("w-full justify-start", {
                "bg-muted": active,
              })}
            >
              <Link
                href={item.href}
                data-testid={item.testId}
                aria-current={active ? "page" : undefined}
              >
                {item.title}
                {item.external ? (
                  <span className="text-muted-foreground ml-2 text-xs">↗</span>
                ) : null}
              </Link>
            </Button>
          )
        })}
      </nav>
    </>
  )
}
